if (!customElements.get('slideshow-component')) {
  customElements.define(
    'slideshow-component',
    class SlideshowComponent extends HTMLElement {
      constructor() {
        super();

        this.flkty = null;

        this.flktyResizeEvent = () => {
          if (!this.flkty) return;

          this.flkty.resize();
        };

        this.onResizeWidthEvent = () => this.onResizeWidth();
      }

      connectedCallback() {
        this.items = this.querySelectorAll('[data-slide]');

        this.getTallestSlideItem();
        this.initSlider();
        this.bindScrollButton();

        document.addEventListener('theme:resize:width', this.onResizeWidthEvent);
      }

      onResizeWidth() {
        this.getTallestSlideItem();
        this.resizeFlickity();
      }

      resizeFlickity() {
        if (!this.flkty) return;

        if (this.flkty.isAnimating || this.flkty.isDragging) {
          this.flkty.off('settle', this.flktyResizeEvent);
          this.flkty.once('settle', this.flktyResizeEvent);
          return;
        }

        this.flktyResizeEvent();
      }

      initSlider() {
        const slidesCount = this.items.length;
        const duration = parseInt(this.dataset.duration);
        const pageDots = this.dataset.pageDots === 'true' && slidesCount > 1;
        const prevNextButtons = this.dataset.navArrows === 'true' && slidesCount > 1;
        let autoplay = this.dataset.autoplay === 'true';

        if (autoplay) {
          autoplay = duration;
        }

        if (slidesCount > 1) {
          this.flkty = new window.theme.Flickity(this, {
            fade: true,
            cellSelector: '[data-slide]',
            autoPlay: autoplay,
            wrapAround: true,
            adaptiveHeight: true,
            setGallerySize: true,
            imagesLoaded: true,
            pageDots: pageDots,
            prevNextButtons: prevNextButtons,
            dragThreshold: 25,
            on: {
              ready: () => {
                requestAnimationFrame(this.flktyResizeEvent);
                document.addEventListener('theme:vars', this.flktyResizeEvent); // Update slideshow height after height vars init

                this.handleFirstSlideAnimation(true);
              },
              change: (index) => this.handleSlideChange(index),
            },
          });

          // iOS smooth scrolling fix
          window.theme.flickitySmoothScrolling(this);
        }
      }

      handleSlideChange(index) {
        this.handleFirstSlideAnimation(false);

        this.flkty.cells.forEach((slide, i) => {
          slide.element.querySelectorAll('a, button').forEach((link) => {
            link.setAttribute('tabindex', i === index ? '0' : '-1');
          });

          slide.element.querySelectorAll('text-highlight').forEach((highlight) => {
            highlight.setTriggerAttribute(Boolean(i === index));
          });
        });
      }

      getTallestSlideItem() {
        if (this.items.length <= 1) return;

        // Remove the container's custom property to let each slide item use the `var(--min-h)` fallback value for their `min-height`
        this.style.removeProperty('--item-min-h');

        const items = [...this.items];
        const heights = items.map((item) => Math.floor(item.offsetHeight));
        const tallestSlideItemHeight = Math.max(...heights);
        const isOriginalHeight = items.some((item) => item.classList.contains('original-height'));
        const isOriginalHeightMobile = items.some((item) => item.classList.contains('original-height-mobile'));
        const hasMobileHeight = this.classList.contains('has-mobile-height');

        if (
          (window.theme.isDesktop() && !isOriginalHeight) ||
          (window.theme.isMobile() && !isOriginalHeightMobile && hasMobileHeight) ||
          (window.theme.isMobile() && !isOriginalHeight && !hasMobileHeight)
        ) {
          const heightDiffs = items.some((item) => Math.floor(item.offsetHeight) !== tallestSlideItemHeight);

          if (heightDiffs) {
            this.style.setProperty('--item-min-h', `${tallestSlideItemHeight}px`);
          }
        }
      }

      /**
       * Handles image animation in first slide that would be triggered on page load
       *  - uses a fallback class modifier for sliders that contain a first slide with no cached hero images
       *  - that class resets the default slider CSS animation so it won't be executed when `.img-in` class is added when `img.complete` is detected
       *  - gets the first slide's `.lazy-image` container and listens for `transitionend` event of its `<img>` child element
       *  - adds a class modifier after `<img>` transition has completed, when shimmer effect has been removed, that should trigger the hero image animation
       */
      handleFirstSlideAnimation(onLoad = false) {
        if (!onLoad) {
          this.classList.remove('slider--no-cached-images');
          this.classList.remove('slider--img-loaded');
          return;
        }

        const firstSlide = this.items[0];
        const slideImage = firstSlide.querySelectorAll('.lazy-image');
        const slideImgComplete = this.classList.contains('img-in');

        if (slideImage.length && !slideImgComplete) {
          this.classList.add('slider--no-cached-images');

          const onImageTransitionEnd = (event) => {
            requestAnimationFrame(() => this.classList.add('slider--img-loaded'));
            slideImage[0].removeEventListener('transitionend', onImageTransitionEnd);
          };

          slideImage[0].addEventListener('transitionend', onImageTransitionEnd);
        }
      }

      // Scroll down function
      bindScrollButton() {
        const arrowDown = this.querySelector('[data-scroll-down]');

        if (arrowDown) {
          arrowDown.addEventListener('click', (e) => {
            e.preventDefault();
            const header = document.querySelector('[data-site-header]');

            const headerHeight = header.classList.contains('site-header--fixed') ? 60 : 0;
            const scrollToPosition = parseInt(Math.ceil(this.offsetTop + this.offsetHeight - headerHeight));

            window.scrollTo({
              top: scrollToPosition,
              left: 0,
              behavior: 'smooth',
            });
          });
        }
      }

      disconnectedCallback() {
        document.removeEventListener('theme:resize:width', this.onResizeWidthEvent);
        document.removeEventListener('theme:vars', this.flktyResizeEvent);

        if (this.flkty) {
          this.flkty.off('settle', this.flktyResizeEvent);
        }
      }
    }
  );
}
